/**
 * Geocoding Utilities for Location Alerts System
 * 
 * Handles address to coordinate conversion with intelligent caching
 * to minimize Google Maps API costs.
 * 
 * Features:
 * - Cache-first approach (checks Supabase cache before API call)
 * - Address normalization for better cache hits
 * - Error handling and fallbacks
 * - Cost tracking and usage statistics
 */

import { supabase } from '../components/data/supabaseClient';

/**
 * Normalize address for consistent cache lookups
 * Converts to lowercase, removes extra spaces, special chars
 */
const normalizeAddress = (address) => {
  if (!address) return '';
  
  return address
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[^\w\s,.-]/g, '') // Remove special characters except comma, period, hyphen
    .replace(/\s*,\s*/g, ',') // Normalize commas
    .substring(0, 500); // Limit length
};

/**
 * Check if coordinates are valid
 */
const isValidCoordinates = (lat, lng) => {
  return (
    lat !== null && 
    lng !== null && 
    !isNaN(lat) && 
    !isNaN(lng) &&
    lat >= -90 && 
    lat <= 90 && 
    lng >= -180 && 
    lng <= 180
  );
};

/**
 * Look up address in Supabase cache
 * Returns cached coordinates if found, null otherwise
 */
const checkCache = async (address) => {
  try {
    const normalizedAddress = normalizeAddress(address);
    
    if (!normalizedAddress) {
      return null;
    }
    
    const { data, error } = await supabase
      .from('location_geocoding_cache')
      .select('latitude, longitude, place_id, formatted_address, city, times_used')
      .eq('normalized_address', normalizedAddress)
      .maybeSingle();
    
    if (error || !data) {
      return null;
    }
    
    // Update usage stats
    await supabase
      .from('location_geocoding_cache')
      .update({
        times_used: data.times_used + 1,
        last_used_at: new Date().toISOString()
      })
      .eq('normalized_address', normalizedAddress);
    
    console.log('✅ Geocoding cache HIT:', normalizedAddress);
    
    return {
      lat: parseFloat(data.latitude),
      lng: parseFloat(data.longitude),
      place_id: data.place_id,
      formatted_address: data.formatted_address,
      city: data.city,
      fromCache: true
    };
    
  } catch (error) {
    console.error('Cache lookup error:', error);
    return null;
  }
};

/**
 * Save geocoded result to cache
 */
const saveToCache = async (address, result) => {
  try {
    const normalizedAddress = normalizeAddress(address);
    
    if (!normalizedAddress || !result) {
      return;
    }
    
    const { error } = await supabase
      .from('location_geocoding_cache')
      .upsert({
        normalized_address: normalizedAddress,
        address: address,
        latitude: result.lat,
        longitude: result.lng,
        place_id: result.place_id || null,
        formatted_address: result.formatted_address || address,
        city: result.city || null,
        country_code: result.country_code || null,
        times_used: 1,
        last_used_at: new Date().toISOString()
      }, {
        onConflict: 'normalized_address',
        ignoreDuplicates: false
      });
    
    if (error) {
      console.error('Cache save error:', error);
    } else {
      console.log('✅ Saved to geocoding cache:', normalizedAddress);
    }
    
  } catch (error) {
    console.error('Error saving to cache:', error);
  }
};

/**
 * Geocode address using Google Maps API
 * This costs money, so we only call it if cache misses
 */
const geocodeWithGoogle = async (address) => {
  try {
    // Check if Google Maps is loaded
    if (!window.google?.maps?.Geocoder) {
      throw new Error('Google Maps not loaded');
    }
    
    const geocoder = new window.google.maps.Geocoder();
    
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          const addressComponents = results[0].address_components;
          
          // Extract city with comprehensive fallback chain
          // Priority order: locality → postal_town → sublocality → administrative_area_level_2 → administrative_area_level_1
          const cityTypes = [
            'locality',                    // Primary city name (e.g., "Rīga")
            'postal_town',                 // Postal town
            'sublocality',                 // Neighborhood/district (e.g., "Latgales priekšpilsēta")
            'administrative_area_level_2', // Municipality (e.g., "Ķekava novads")
            'administrative_area_level_1'  // Region fallback
          ];

          let cityComponent = null;
          for (const type of cityTypes) {
            const component = addressComponents.find(c => c.types.includes(type));
            if (component?.long_name) {
              cityComponent = component;
              break;
            }
          }

          // If still no city found, try to extract from formatted_address
          let city = cityComponent?.long_name || null;
          if (!city && results[0].formatted_address) {
            // Parse formatted address (e.g., "Piedrujas iela, Latgales priekšpilsēta, Rīga, Latvia")
            const parts = results[0].formatted_address.split(',').map(p => p.trim());
            // Second-to-last part is usually city (last part is country)
            if (parts.length >= 2) {
              city = parts[parts.length - 2];
            }
          }

          // Log for debugging city extraction
          console.log('City extraction:', {
            address,
            extractedCity: city,
            componentType: cityComponent?.types?.[0] || 'none',
            formattedAddress: results[0].formatted_address
          });
          
          // Extract country
          const countryComponent = addressComponents.find(
            component => component.types.includes('country')
          );
          
          const result = {
            lat: location.lat(),
            lng: location.lng(),
            place_id: results[0].place_id,
            formatted_address: results[0].formatted_address,
            city: city,
            country_code: countryComponent?.short_name || null,
            fromCache: false
          };
          
          console.log('✅ Geocoded with Google API:', address);
          
          resolve(result);
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
    
  } catch (error) {
    console.error('Google geocoding error:', error);
    throw error;
  }
};

/**
 * Main geocoding function with cache-first approach
 * 
 * @param {string} address - Address to geocode
 * @param {object} options - Options
 * @param {boolean} options.forceRefresh - Skip cache and force API call
 * @returns {object} Coordinates and metadata
 */
export const geocodeAddress = async (address, options = {}) => {
  const { forceRefresh = false } = options;
  
  try {
    // Validate input
    if (!address || typeof address !== 'string') {
      throw new Error('Invalid address provided');
    }
    
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = await checkCache(address);
      if (cached) {
        return cached;
      }
    }
    
    // Cache miss - call Google API
    console.log('💰 Cache MISS - calling Google API:', address);
    const result = await geocodeWithGoogle(address);
    
    // Validate result
    if (!isValidCoordinates(result.lat, result.lng)) {
      throw new Error('Invalid coordinates returned from API');
    }
    
    // Save to cache for next time
    await saveToCache(address, result);
    
    return result;
    
  } catch (error) {
    console.error('Geocoding error for address:', address, error);
    throw error;
  }
};

/**
 * Geocode multiple addresses in batch
 * Useful for bulk operations
 * 
 * @param {array} addresses - Array of addresses
 * @returns {array} Array of results (null for failures)
 */
export const geocodeAddresses = async (addresses) => {
  const results = [];
  
  for (const address of addresses) {
    try {
      const result = await geocodeAddress(address);
      results.push(result);
      
      // Add small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
      
    } catch (error) {
      console.error('Failed to geocode:', address, error);
      results.push(null);
    }
  }
  
  return results;
};

/**
 * Get coordinates from address with user-friendly error messages
 * This is the main function to use in components
 * 
 * @param {string} address - Address to geocode
 * @returns {object} { success, data, error }
 */
export const getCoordinates = async (address) => {
  try {
    if (!address) {
      return {
        success: false,
        error: 'Address is required',
        data: null
      };
    }
    
    const result = await geocodeAddress(address);
    
    return {
      success: true,
      data: {
        latitude: result.lat,
        longitude: result.lng,
        place_id: result.place_id,
        formatted_address: result.formatted_address,
        city: result.city,
        fromCache: result.fromCache
      },
      error: null
    };
    
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to get coordinates',
      data: null
    };
  }
};

/**
 * Get cache statistics (useful for monitoring)
 */
export const getCacheStats = async () => {
  try {
    const { data, error } = await supabase
      .from('location_geocoding_cache')
      .select('id, times_used, created_at');
    
    if (error) throw error;
    
    const totalEntries = data.length;
    const totalUsage = data.reduce((sum, entry) => sum + entry.times_used, 0);
    const avgUsage = totalEntries > 0 ? (totalUsage / totalEntries).toFixed(2) : 0;
    
    return {
      totalEntries,
      totalUsage,
      avgUsage,
      estimatedSavings: (totalUsage - totalEntries) * 0.005 // $0.005 per request
    };
    
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return null;
  }
};

/**
 * Clear old cache entries (older than X days)
 * Run this periodically to keep cache fresh
 */
export const cleanupOldCache = async (daysOld = 180) => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    
    const { error } = await supabase
      .from('location_geocoding_cache')
      .delete()
      .lt('last_used_at', cutoffDate.toISOString())
      .lt('times_used', 3); // Only delete rarely used entries
    
    if (error) throw error;
    
    console.log(`✅ Cleaned up cache entries older than ${daysOld} days`);
    
  } catch (error) {
    console.error('Cache cleanup error:', error);
  }
};

export default {
  geocodeAddress,
  geocodeAddresses,
  getCoordinates,
  getCacheStats,
  cleanupOldCache,
  normalizeAddress,
  isValidCoordinates
};
